<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col">
    <!-- Delete Confirmation -->
    <ConfirmAction
      v-if="showDeleteModal"
      variant="danger"
      :message="
        $t('views.groups.deleteConfirmMessage', {
          count: enrichedGroupGoats.length,
        })
      "
      :confirmMessage="$t('common.buttons.delete')"
      @confirm="executeDelete"
      @cancel="showDeleteModal = false" />

    <div
      v-if="error"
      class="mx-4 my-4 p-4 bg-red-100 text-red-800 rounded-lg shadow">
      {{ error }}
    </div>

    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <div class="text-center">
        <div
          class="w-12 h-12 border-4 border-[var(--color-primary-400)] border-t-[var(--color-primary-900)] rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-[var(--color-text)]">{{ $t('views.groups.loading') }}</p>
      </div>
    </div>

    <div
      v-if="!loading && group"
      class="flex-1 overflow-hidden px-4 py-6 flex flex-col gap-6">
      <!-- Group Information -->
      <ModuleShell :title="group.name" :icon="Layers">
        <!-- Delete Group Action -->
        <template #action>
          <DeleteGroupAction @click="showDeleteModal = true" />
        </template>

        <div class="flex flex-col gap-4">
          <div class="flex items-center gap-2 text-[var(--color-text)]">
            <div
              class="bg-[var(--color-primary-100)] p-1.5 rounded-md text-[var(--color-primary-600)]">
              <MapPin class="w-4 h-4" />
            </div>
            <span class="font-medium">
              {{ group.location || $t('views.groups.notSpecified') }}
            </span>
          </div>

          <div
            class="h-px bg-[var(--color-primary-200)] w-full opacity-60"></div>

          <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p
              v-if="group.description"
              class="text-sm text-[var(--color-text)] italic opacity-80 max-w-lg">
              "{{ group.description }}"
            </p>
            <p
              v-else
              class="text-sm text-[var(--color-text)] opacity-40 italic">
              {{ $t('views.groups.noDescription') }}
            </p>

            <div
              class="self-start sm:self-auto bg-[var(--color-primary-50)] border border-[var(--color-primary-200)] px-3 py-1.5 rounded-lg flex items-center gap-2 text-[var(--color-primary-800)] shadow-sm">
              <Footprints class="w-4 h-4" />
              <span class="font-bold text-sm">
                {{ enrichedGroupGoats.length }} {{ $t('views.goats.title') }}
              </span>
            </div>
          </div>
        </div>
      </ModuleShell>

      <div class="flex-1 flex flex-col min-h-0 pb-4">
        <!-- Empty State with Icon-->
        <div
          v-if="enrichedGroupGoats.length === 0"
          class="flex-1 flex flex-col items-center justify-center text-center p-8 bg-white/50 rounded-xl border border-dashed border-[var(--color-primary-400)]">
          <div class="bg-[var(--color-primary-100)] p-4 rounded-full mb-3">
            <Footprints class="w-8 h-8 text-[var(--color-primary-500)]" />
          </div>

          <h3 class="font-semibold text-[var(--color-primary-900)]">
            {{ $t('views.groups.noGoatsInGroup') }}
          </h3>

          <p class="text-sm text-[var(--color-text)] opacity-70 mt-1">
            {{ $t('views.groups.addGoatsToGroup') }}
          </p>
        </div>
        <!--Integrated List Module with Search and Refresh -->
        <ListTableModule
          v-else
          :data="enrichedGroupGoats"
          :columns="tableColumns"
          :loading="loading"
          @row-click="handleRowClick"
          @refresh="refreshData"
          class="h-full">

          <template #cell-eartag="{ value }">
            <span :title="value" class="font-mono">
              ..{{ value ? String(value).slice(-5) : '-' }}
            </span>
          </template>

          <template #cell-gender="{ value }">
            <span
              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold gap-1"
              :class="
                value === 'male'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-pink-100 text-pink-800'
              ">
              <Mars v-if="value === 'male'" class="w-3 h-3 fill-current" />
              <Venus v-else class="w-3 h-3 fill-current" />
              {{
                value === 'male'
                  ? $t('views.goats.table.male')
                  : $t('views.goats.table.female')
              }}
            </span>
          </template>
        </ListTableModule>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted,watch } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useGroupStore } from '@/stores/group.store.js'
  import { useGoatStore } from '@/stores/goat.store.js'
  import { useUiStore } from '@/stores/ui.store.js'
  import { Mars, Venus, MapPin, Layers, Footprints } from 'lucide-vue-next'

  // Components
  import BackButton from '@/components/ui/buttons/GetBackButton.vue'
  import ConfirmAction from '@/components/actions/ConfirmAction.vue'
  import DeleteGroupAction from '@/components/groups/DeleteGroupAction.vue'
  import ListTableModule from '@/components/ui/tables/ListTable.vue'
  import ModuleShell from '@/components/ui/layout/ModuleShell.vue'

  const router = useRouter()
  const route = useRoute()
  const { t } = useI18n()
  const groupStore = useGroupStore()
  const goatStore = useGoatStore()
  const uiStore = useUiStore()

  const loading = ref(true)
  const error = ref(null)
  const showDeleteModal = ref(false)

  const groupId = computed(() => parseInt(route.params.groupId))
  const group = computed(() =>
    groupStore.groups.find((g) => g.id === groupId.value)
  )

  const enrichedGroupGoats = computed(() => {
    const goatsInGroup = goatStore.goats.filter(
      (goat) => goat.groupId === groupId.value
    )
    return goatsInGroup.map((goat) => ({
      ...goat,
      groupName: group.value ? group.value.name : '-',
    }))
  })

  // Header Logic
  watch(group, (g) => {
    if (!g) return

    uiStore.setHeader({
      title: t('views.groups.titledetail'), 
      subtitle: g.name, 
      showBack: true
    })
  }, { immediate: true })


  onMounted(async () => {
    try {
      loading.value = true
      if (groupStore.groups.length === 0) await groupStore.fetchGroups()
      if (goatStore.goats.length === 0) await goatStore.fetchGoats()

      if (!group.value) error.value = t('views.groups.groupNotFound')
    } catch (err) {
      error.value = t('views.groups.loadError')
    } finally {
      loading.value = false
    }
  })

  // Table Config
  const tableColumns = [
    {
      key: 'eartag',
      label: 'views.goats.table.eartag',
      type: 'text',
      simple: true,
    },
    {
      key: 'name',
      label: 'views.goats.table.name',
      type: 'text',
      simple: true,
    },
    {
      key: 'dateOfBirth',
      label: 'views.goats.table.birthDate',
      type: 'date',
      simple: true,
    },
    { key: 'gender', label: 'views.goats.table.gender', type: 'text' },
    { key: 'breed', label: 'views.goats.table.breed', type: 'text' },
  ]

  // Actions
  async function refreshData() {
    loading.value = true
    await Promise.all([groupStore.fetchGroups(), goatStore.fetchGoats()])
    loading.value = false
  }

  function handleRowClick(goat) {
    router.push({ name: 'GoatDetail', params: { id: goat.id } })
  }

  //Delete Function
  async function executeDelete() {
    if (!group.value) return
    try {
      loading.value = true
      const success = await groupStore.deleteGroup(group.value.id)
      if (success) {
        await goatStore.fetchGoats()
        router.push('/groups')
      } else {
        error.value = t('views.groups.deleteFailed')
      }
    } catch (err) {
      error.value = err.message
    } finally {
      loading.value = false
      showDeleteModal.value = false
    }
  }

  // Init
  onMounted(async () => {
    try {
      loading.value = true
      if (groupStore.groups.length === 0) await groupStore.fetchGroups()
      if (goatStore.goats.length === 0) await goatStore.fetchGoats()

      if (!group.value) error.value = t('views.groups.groupNotFound')
    } catch (err) {
      error.value = t('views.groups.loadError')
    } finally {
      loading.value = false
    }
  })
</script>
